import { listAllLegTripsForOrder, supersedeLegTrip } from "@/lib/leg-trip-sync";
import { isAlreadyCancelledUpstreamError } from "@/lib/eapi-cancel-utils";

export { isAlreadyCancelledUpstreamError };

/**
 * Per-leg cancel fan-out: cancel every leg-attached EAPI trip on `orderId`
 * (active OR locally superseded by a non-cancel reason) whose `eapi_trip_id`
 * differs from a trip the caller already cancelled, and supersede the
 * corresponding `trips` rows.
 *
 * This intentionally walks ALL recent trips for the order — not just the
 * `is_active = true` set — so that orphaned upstream trips left behind by
 * earlier pivots / direct `eapi_trip_id` clears still get cancelled here.
 * Trips already superseded with reason `order_cancelled` / `no_eapi_switch`
 * are skipped (we already cancelled those upstream).
 *
 * Best-effort — failures on individual leg trips are logged but never throw,
 * since the order-level cancel already represents the user's intent during
 * the dual-write window.
 */
const ALREADY_CANCELLED_REASONS = new Set([
  "order_cancelled",
  "no_eapi_switch",
  // A single leg cancelled from the Update Status modal already retired its
  // upstream trip; a later order-level cancel must not try again.
  "leg_cancelled",
]);

export async function cancelOtherActiveLegTrips(
  orderId: string,
  alreadyCancelledTripId: string | null,
  client: {
    cancelTrip: (id: string, reason?: string) => Promise<unknown>;
    abandonTrip?: (id: string, reason?: string) => Promise<unknown>;
  },
  reason: string,
  supersedeReason: string,
  mode: "cancel" | "abandon" = "cancel",
): Promise<void> {
  const all = await listAllLegTripsForOrder(orderId);
  const seen = new Set<string>();
  const failures: Array<{ leg_sequence: number; eapi_trip_id: string; message: string }> = [];
  for (const t of all) {
    if (alreadyCancelledTripId && t.eapi_trip_id === alreadyCancelledTripId) {
      if (t.is_active) await supersedeLegTrip(t.trip_row_id, supersedeReason);
      seen.add(t.eapi_trip_id);
      continue;
    }
    if (!t.is_active && t.superseded_reason && ALREADY_CANCELLED_REASONS.has(t.superseded_reason)) {
      // Already cancelled upstream by us in a prior step — nothing to do.
      seen.add(t.eapi_trip_id);
      continue;
    }
    if (seen.has(t.eapi_trip_id)) {
      // Same upstream trip referenced by multiple rows — only cancel once.
      if (t.is_active) await supersedeLegTrip(t.trip_row_id, supersedeReason);
      continue;
    }
    seen.add(t.eapi_trip_id);
    let cancelledUpstream = false;
    try {
      if (mode === "abandon") {
        if (!client.abandonTrip) {
          throw new Error("abandonTrip not implemented on EAPI client");
        }
        await client.abandonTrip(t.eapi_trip_id, reason);
      } else {
        await client.cancelTrip(t.eapi_trip_id, reason);
      }
      cancelledUpstream = true;
      console.log(`[per-leg ${mode}] ${mode === "abandon" ? "abandoned" : "cancelled"} leg ${t.leg_sequence} trip`, t.eapi_trip_id);
    } catch (err: any) {
      if (isAlreadyCancelledUpstreamError(err)) {
        cancelledUpstream = true;
        console.log(
          `[per-leg ${mode}] leg ${t.leg_sequence} trip ${t.eapi_trip_id} already cancelled upstream, treating as success`,
        );
      } else {
        console.warn(
          `[per-leg ${mode}] failed to ${mode} leg ${t.leg_sequence} trip ${t.eapi_trip_id}:`,
          err?.message || err,
        );
        failures.push({
          leg_sequence: t.leg_sequence,
          eapi_trip_id: t.eapi_trip_id,
          message: err?.message ?? String(err),
        });
      }
    }
    // Only mark the local trip row as superseded once the upstream cancel
    // succeeded (or the trip was already cancelled upstream). Otherwise we
    // would clear our local pointer to an upstream trip that is still live.
    if (cancelledUpstream && t.is_active) {
      await supersedeLegTrip(t.trip_row_id, supersedeReason);
    }
  }
  if (failures.length > 0) {
    const detail = failures
      .map((f) => `leg ${f.leg_sequence} (${f.eapi_trip_id}): ${f.message}`)
      .join("; ");
    throw new Error(
      `Failed to ${mode} ${failures.length} upstream EAPI trip(s): ${detail}`,
    );
  }
}